import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from '@/components/ui/toaster';
import { useAuth } from '@/features/auth/AuthProvider';
import { useCurrentStore } from '@/hooks/useCurrentStore';
import {
  fetchAllInventory,
  fetchStoreInventoryMap,
  mergeWithStoreStock,
  recordStockMovement,
} from '../inventoryQueries';
import {
  bulkSetSerializedUnavailable,
  fetchAvailableSerializedItems,
} from '../serializedQueries';

export function AdjustOutTab() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const currentStoreId = useCurrentStore((s) => s.currentStoreId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const [selectedSerialIds, setSelectedSerialIds] = useState<string[]>([]);

  const { data: items = [] } = useQuery({ queryKey: ['inventory'], queryFn: fetchAllInventory });
  const { data: storeMap = {} } = useQuery({
    queryKey: ['store-inventory-map', currentStoreId],
    queryFn: () => (currentStoreId ? fetchStoreInventoryMap(currentStoreId) : Promise.resolve({})),
    enabled: !!currentStoreId,
  });
  const withStock = mergeWithStoreStock(items, storeMap).filter((i) => i.quantity > 0);
  const selected = withStock.find((i) => i.id === selectedId);

  const { data: availableSerials = [] } = useQuery({
    queryKey: ['serialized-available', selectedId, currentStoreId],
    queryFn: () =>
      selectedId && currentStoreId
        ? fetchAvailableSerializedItems(selectedId, currentStoreId)
        : Promise.resolve([]),
    enabled: !!selectedId && !!currentStoreId,
  });

  const adjustOut = useMutation({
    mutationFn: async () => {
      if (!selectedId || !currentStoreId) throw new Error('Select product');
      if (!note.trim()) throw new Error('Note is required');
      const serialNumbers =
        selectedSerialIds.length > 0
          ? availableSerials.filter((s) => selectedSerialIds.includes(s.id)).map((s) => s.serialNumber)
          : undefined;
      const deductQty = serialNumbers?.length ? serialNumbers.length : qty;

      await recordStockMovement({
        itemId: selectedId,
        storeId: currentStoreId,
        type: 'MANUAL_OUT',
        quantity: deductQty,
        note: note.trim(),
        serialNumbers,
        actorId: user?.uid,
      });

      if (selectedSerialIds.length > 0) {
        await bulkSetSerializedUnavailable(selectedSerialIds);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['store-inventory-map'] });
      qc.invalidateQueries({ queryKey: ['stock-movements'] });
      qc.invalidateQueries({ queryKey: ['serialized-available'] });
      toast({ title: 'Stock adjusted out', variant: 'success' });
      setNote('');
      setSelectedSerialIds([]);
    },
    onError: (e) => toast({ title: 'Failed', description: String(e), variant: 'destructive' }),
  });

  if (!currentStoreId) {
    return <p className="p-8 text-center text-sm text-muted-foreground">Select a store from the topbar.</p>;
  }

  return (
    <Card className="p-4 max-w-xl">
      <Label>Select product</Label>
      <select
        className="mt-1 w-full h-9 rounded-md border px-2 text-sm"
        value={selectedId ?? ''}
        onChange={(e) => {
          setSelectedId(e.target.value || null);
          setSelectedSerialIds([]);
        }}
      >
        <option value="">Choose…</option>
        {withStock.map((i) => (
          <option key={i.id} value={i.id}>
            {i.sku} — {i.brand} ({i.quantity})
          </option>
        ))}
      </select>

      {selected && (
        <div className="mt-4 space-y-3">
          <p className="text-sm">Available: <strong>{selected.quantity}</strong></p>
          <div>
            <Label>Quantity</Label>
            <Input type="number" min={1} max={selected.quantity} value={qty} onChange={(e) => setQty(Number(e.target.value))} />
          </div>
          <div>
            <Label>Note *</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for adjustment" />
          </div>

          {availableSerials.length > 0 && (
            <div>
              <Label className="mb-2 block">Or select serial units</Label>
              <div className="flex flex-wrap gap-2">
                {availableSerials.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`rounded border px-2 py-1 text-xs ${selectedSerialIds.includes(s.id) ? 'bg-gold-100 border-gold-500' : ''}`}
                    onClick={() =>
                      setSelectedSerialIds((prev) =>
                        prev.includes(s.id) ? prev.filter((x) => x !== s.id) : [...prev, s.id],
                      )
                    }
                  >
                    {s.serialNumber}
                  </button>
                ))}
              </div>
            </div>
          )}

          <Button variant="gold" disabled={adjustOut.isPending} onClick={() => adjustOut.mutate()}>
            Adjust out
          </Button>
        </div>
      )}
    </Card>
  );
}
